import { localized, msg } from '@lit/localize';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { toDisplayString } from '../utils/binary-display.js';
import '@spectrum-web-components/table/sp-table.js';
import '@spectrum-web-components/table/sp-table-head.js';
import '@spectrum-web-components/table/sp-table-head-cell.js';
import '@spectrum-web-components/table/sp-table-body.js';
import '@spectrum-web-components/table/sp-table-row.js';
import '@spectrum-web-components/table/sp-table-cell.js';

@customElement('resizable-table')
@localized()
export class ResizableTable extends LitElement {
  static styles = css`
    :host {
      display: block;
      min-width: 0;
      width: 100%;
    }
    .table-shell {
      background: var(--color-sidebar-surface);
      border: 1px solid var(--color-app-border);
      border-radius: var(--radius-panel, 8px);
      box-sizing: border-box;
      max-width: 100%;
      overflow: auto;
      scrollbar-width: thin;
    }
    sp-table {
      border-collapse: separate;
      border-spacing: 0;
      color: var(--color-app-text);
      display: table;
      font-family: var(--font);
      font-size: 12px;
      line-height: 1.4;
      min-width: max-content;
      width: 100%;
    }
    sp-table-head-cell,
    sp-table-cell {
      box-sizing: border-box;
      cursor: text;
      min-width: 86px;
      overflow: hidden;
      padding: 9px 12px;
      position: relative;
      text-align: left;
      text-overflow: ellipsis;
      user-select: text;
      -webkit-user-select: text;
      -moz-user-select: text;
      -ms-user-select: text;
      vertical-align: top;
      white-space: normal;
    }
    .table-resizer {
      background: transparent;
      cursor: col-resize;
      height: 100%;
      position: absolute;
      right: -4px;
      top: 0;
      user-select: none;
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      width: 8px;
      z-index: 3;
    }
    .table-resizer::after {
      background: var(--color-app-border);
      content: '';
      display: block;
      height: 100%;
      margin: 0 auto;
      opacity: 0.7;
      transition: background-color 0.2s;
      width: 1px;
    }
    .table-resizer:hover::after,
    .table-resizer:active::after {
      background-color: var(--color-sidebar-selected-border);
      opacity: 0.8;
    }
    sp-table-head-cell {
      background: var(--color-sidebar-bg);
      border-bottom: 1px solid var(--color-app-border);
      box-shadow: inset -1px 0 0 var(--color-app-border);
      color: var(--color-app-text);
      font-weight: 700;
      line-height: 1.25;
      position: sticky;
      top: 0;
      z-index: 2;
    }
    sp-table-head-cell:last-child,
    sp-table-cell:last-child {
      box-shadow: none;
    }
    sp-table-body sp-table-row {
      border-bottom: 1px solid var(--color-app-border);
    }
    sp-table-body sp-table-row:last-child {
      border-bottom: none;
    }
    sp-table-body sp-table-row sp-table-cell {
      background: var(--color-sidebar-surface);
      border-bottom: 1px solid var(--color-app-border);
      box-shadow: inset -1px 0 0 var(--color-app-border);
      color: var(--color-app-text);
    }
    sp-table-body sp-table-row:nth-child(even) sp-table-cell {
      background: color-mix(
        in srgb,
        var(--color-sidebar-surface) 86%,
        var(--color-sidebar-bg)
      );
    }
    sp-table-body sp-table-row:hover sp-table-cell {
      background: var(--color-sidebar-surface-hover);
    }
    sp-table-body sp-table-row:last-child sp-table-cell {
      border-bottom: none;
    }
    .cell-value {
      line-clamp: 2;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      max-height: 3em;
      display: -webkit-box;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: normal;
      word-break: break-word;
    }
    .cell-value.empty {
      color: var(--color-app-text-secondary);
    }
    .empty-state {
      align-items: center;
      color: var(--color-app-text-secondary);
      display: flex;
      font-family: var(--font);
      font-size: 12px;
      justify-content: center;
      min-height: 72px;
      padding: var(--gap-2);
    }
  `;

  @property({ attribute: false }) accessor data: unknown[] = [];

  @property({ type: String }) accessor tableKey: string = '';

  @state() private accessor columnWidths: Record<string, number> = {};

  private columnResizeState: {
    isResizing: boolean;
    columnIndex: number;
    columnName: string;
    startX: number;
    startWidth: number;
  } | null = null;

  private getTableColumns(items: unknown[]): string[] {
    if (Array.isArray(items) && items.length > 0) {
      const columns = Object.keys(items[0] as Record<string, unknown>);
      if (columns.includes('id')) {
        columns.splice(columns.indexOf('id'), 1);
        columns.unshift('id');
      }
      return columns;
    }
    return [];
  }

  private formatTableCellValue(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }
    return toDisplayString(value);
  }

  private formatColumnLabel(column: string): string {
    return column
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .trim();
  }

  private getColumnWidth(column: string): number {
    const savedWidth = this.columnWidths[column];
    if (savedWidth) {
      return savedWidth;
    }

    const normalized = column.toLowerCase();
    if (normalized === 'id' || normalized.endsWith('id')) {
      return 150;
    }
    if (
      normalized.includes('timestamp') ||
      normalized.includes('date') ||
      normalized.includes('time') ||
      normalized.includes('created') ||
      normalized.includes('updated') ||
      normalized.includes('since')
    ) {
      return 168;
    }
    if (
      normalized.includes('name') ||
      normalized.includes('subject') ||
      normalized.includes('title') ||
      normalized.includes('note')
    ) {
      return 184;
    }
    if (
      normalized.includes('status') ||
      normalized.includes('type') ||
      normalized.includes('number')
    ) {
      return 112;
    }
    return 140;
  }

  private handleColumnResizeStart = (
    e: MouseEvent,
    columnName: string,
    columnIndex: number
  ) => {
    const target = e.target as HTMLElement;
    if (!target.classList.contains('table-resizer')) {
      return;
    }

    const headerCell = target.closest('sp-table-head-cell') as HTMLElement;
    if (!headerCell) return;

    const currentWidth = headerCell.offsetWidth;
    const savedWidth = this.columnWidths[columnName];

    this.columnResizeState = {
      isResizing: true,
      columnIndex,
      columnName,
      startX: e.clientX,
      startWidth: savedWidth || currentWidth,
    };

    document.addEventListener('mousemove', this.handleColumnResizeMove);
    document.addEventListener('mouseup', this.handleColumnResizeEnd);
    e.preventDefault();
    e.stopPropagation();
  };

  private handleColumnResizeMove = (e: MouseEvent) => {
    if (!this.columnResizeState?.isResizing) return;

    const deltaX = e.clientX - this.columnResizeState.startX;
    const newWidth = Math.max(72, this.columnResizeState.startWidth + deltaX);

    this.columnWidths = {
      ...this.columnWidths,
      [this.columnResizeState.columnName]: newWidth,
    };
  };

  private handleColumnResizeEnd = () => {
    if (this.columnResizeState) {
      this.columnResizeState.isResizing = false;
    }
    document.removeEventListener('mousemove', this.handleColumnResizeMove);
    document.removeEventListener('mouseup', this.handleColumnResizeEnd);
  };

  render() {
    if (!Array.isArray(this.data) || this.data.length === 0) {
      return html`<div class="empty-state">${msg('No data to display')}</div>`;
    }

    const columns = this.getTableColumns(this.data);

    if (columns.length === 0) {
      return html`<div class="empty-state">${msg('No valid table data')}</div>`;
    }

    return html`
      <div class="table-shell">
        <sp-table>
          <sp-table-head>
            <sp-table-row>
              ${columns.map((col, index) => {
                const width = this.getColumnWidth(col);
                const style = `width: ${width}px; max-width: ${width}px;`;
                return html`
                  <sp-table-head-cell
                    style=${style}
                    title=${this.formatColumnLabel(col)}
                  >
                    ${this.formatColumnLabel(col)}
                    ${
                      index < columns.length - 1
                        ? html`
                            <div
                              class="table-resizer"
                              @mousedown=${(e: MouseEvent) => {
                                e.stopPropagation();
                                this.handleColumnResizeStart(e, col, index);
                              }}
                            ></div>
                          `
                        : nothing
                    }
                  </sp-table-head-cell>
                `;
              })}
            </sp-table-row>
          </sp-table-head>
          <sp-table-body>
            ${this.data.map(
              (item) => html`
                <sp-table-row>
                  ${columns.map((col) => {
                    const value =
                      typeof item === 'object' && item !== null
                        ? (item as Record<string, unknown>)[col]
                        : undefined;
                    const displayValue = this.formatTableCellValue(value);
                    const width = this.getColumnWidth(col);
                    const style = `width: ${width}px; max-width: ${width}px;`;
                    return html`
                      <sp-table-cell style=${style} title=${displayValue}>
                        <span
                          class="cell-value ${displayValue ? '' : 'empty'}"
                        >
                          ${displayValue || '—'}
                        </span>
                      </sp-table-cell>
                    `;
                  })}
                </sp-table-row>
              `
            )}
          </sp-table-body>
        </sp-table>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'resizable-table': ResizableTable;
  }
}
